import {
  Button,
  Card,
  PageContainer,
  PageHeader,
  Typography,
} from '@sl/react-datafabric-components';
import { Tooltip } from 'primereact/tooltip';
import { classNames } from 'primereact/utils';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInsightContext } from '../../../common/context/insight-context.provider';
import {
  BillingCycle,
  formatCycleLabel,
  formatPlanCyclePrice,
  getPlanCyclePriceID,
} from '../../../common/subscriptions/plan-pricing';
import { useFetchPlansQuery } from '../../../common/redux/services/plans';
import { useFetchProfileQuery } from '../../../common/redux/services/profile';
import { useFetchCurrentUserSubscriptionsQuery } from '../../../common/redux/services/subscriptions';
import styles from './subscriptions.module.css';

export type Subscription = {
  id: string;
  plan: {
    name: string;
    roleID: string;
    priority: number;
    price: number;
    trial: boolean;
    type: string;
    nTokens: number;
    isDeleted: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
    description: string;
    metadata: {
      subscription: {
        productID: string;
        priceID?: string;
        priceId?: string;
        defaultBillingCycle?: string;
        prices?: Record<string, string>;
        pricing?: Record<string, number>;
        amounts?: Record<string, number>;
      };
    };
    id: string;
  };
  itemName: string;
  itemType: string;
  itemID: string;
  startDate: string;
  renewalDate: string;
  status: string;
  currentTokens: number;
  autoTopUp?: boolean;
  autoTopUpValue?: number;
};

const Content = ({
  name,
  description,
}: {
  name: string;
  description?: string;
}) => (
  <div className={styles.planCardContent}>
    <Typography type={3} className={styles.planCardTitle}>
      {name.toUpperCase()}
    </Typography>

    <div className='w-full mt-2'>
      <Typography className={styles.planCardDescription}>
        {description}
      </Typography>
    </div>
  </div>
);

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const { currentContext, currentContextAgentID } = useInsightContext();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  const { data } = useFetchPlansQuery({});
  const { data: userSubscriptions } = useFetchCurrentUserSubscriptionsQuery(
    currentContextAgentID ? { itemID: currentContextAgentID } : {},
  );

  const { data: user } = useFetchProfileQuery('');

  // Check isSuperAdmin from multiple possible locations
  const isSuperAdmin =
    user?.data?.isSuperAdmin === true ||
    user?.data?.userRoles?.some((role: any) => role.isSuperAdmin === true) ||
    user?.data?.roles?.some((role: any) => role.isSuperAdmin === true);
  const activeSubscriptions = (userSubscriptions?.data || []).filter(
    (sub) => String(sub?.status || '').toLowerCase() === 'active',
  );
  const activePlanIDs = activeSubscriptions
    .map((sub) => sub?.plan?.id)
    .filter(Boolean);
  const hasActiveSubscription = activePlanIDs.length > 0;

  return (
    <PageContainer>
      <PageHeader
        title='Subscriptions'
        description={
          currentContext
            ? `Manage subscription for ${currentContext.name}.`
            : 'Learn more about our subscription options.'
        }
      />

      <div className={styles.billingCycleToggle}>
        <button
          type='button'
          onClick={() => setBillingCycle('monthly')}
          className={classNames(styles.cycleButton, {
            [styles.cycleButtonActive]: billingCycle === 'monthly',
          })}
        >
          Monthly
        </button>
        <button
          type='button'
          onClick={() => setBillingCycle('yearly')}
          className={classNames(styles.cycleButton, {
            [styles.cycleButtonActive]: billingCycle === 'yearly',
          })}
        >
          Yearly
        </button>
      </div>

      {data?.data && data.data.length > 0 && (
        <div className='p-1 flex flex-wrap gap-4 justify-content-evenly'>
          <Tooltip
            target='.tooltip-report'
            pt={{ root: { className: 'max-w-15rem' } }}
          />
              {[...data.data]
            .sort((a, b) => a.priority - b.priority)
            .map((plan) => {
                const {
                  id,
                  name,
                  roleID,
                  description,
                  metadata,
                } = plan;
                const isActivePlan = activePlanIDs?.includes(id);
                const priorityColor = isActivePlan ? '#ebf8ef' : '#ffffff';
                const priorityBorderColor = isActivePlan
                  ? 'rgba(34, 197, 94, 0.4)'
                  : '#dbe1ee';
                const selectedPriceLabel = formatPlanCyclePrice(plan, billingCycle);
                const selectedPriceID = getPlanCyclePriceID(plan, billingCycle);
                const selectedPlan = {
                  id,
                  name,
                  roleID,
                  metadata,
                  billingCycle,
                  priceID: selectedPriceID,
                };
                const openSubscriptionFlow = () =>
                  navigate('/subscriptions/subscribe', {
                    state: {
                      selectedPlan,
                      ...(hasActiveSubscription
                        ? { allowExistingSubscriptionFlow: true }
                        : {}),
                    },
                  });

                const handleCardClick = (event?: any) => {
                  const target = event?.target as HTMLElement | null;
                  if (
                    target?.closest(
                      'button, a, input, textarea, select, [role="button"]',
                    )
                  ) {
                    return;
                  }

                  if (isSuperAdmin) return;

                  if (isActivePlan) {
                    navigate('/subscriptions/manage-subscription', {
                      state: { selectedPlan },
                    });
                    return;
                  }

                  openSubscriptionFlow();
                };

                const header = (
                  <img
                    alt='Card Header'
                    src='/logo-small.png'
                    className={classNames(
                      styles.ghost,
                      'w-5 mx-auto p-2 rounded-t-lg'
                    )}
                  />
                );
                let footer: JSX.Element | null = null;
                footer = null;

                if (isSuperAdmin) {
                  footer = null;
                } else if (isActivePlan) {
                  footer = (
                    <div className='flex justify-between mt-2'>
                      <Button
                        label='Manage Subscription'
                        icon='pi pi-wrench'
                        severity='success'
                        // style={{ background: 'white', color: '#fff' }}
                        onClick={(event) => {
                          event?.stopPropagation?.();
                          navigate('/subscriptions/manage-subscription', {
                            state: { selectedPlan },
                          });
                        }}
                      />
                    </div>
                  );
                } else if (!hasActiveSubscription) {
                  footer = (
                    <div className='flex justify-between mt-2'>
                      <Button
                        label='Start Subscription'
                        icon='pi pi-arrow-right'
                        severity='info'
                        onClick={(event) => {
                          event?.stopPropagation?.();
                          openSubscriptionFlow();
                        }}
                      />
                    </div>
                  );
                } else {
                  footer = (
                    <div className='flex justify-between mt-2'>
                      <Button
                        label='Upgrade'
                        icon='pi pi-arrow-up'
                        severity='info'
                        onClick={(event) => {
                          event?.stopPropagation?.();
                          openSubscriptionFlow();
                        }}
                      />
                    </div>
                  );
                }

                return (
                  <Card
                    key={id}
                    subTitle={`${selectedPriceLabel} (${formatCycleLabel(
                      billingCycle,
                    )})`}
                    footer={footer}
                    header={header}
                    pt={{
                      header: { className: 'flex align-items-center pt-4' },
                    }}
                    className={`md:w-25rem shadow-6 hover:shadow-8 border-round-2xl ${styles.planCard} ${
                      isActivePlan ? styles.activePlan : ''
                    } ${!isSuperAdmin ? styles.planCardClickable : ''}`}
                    style={{
                      backgroundColor: priorityColor,
                      borderColor: priorityBorderColor,
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      gridColumn: 'span 1',
                    }}
                    onClick={(event) => handleCardClick(event)}
                    role={isSuperAdmin ? undefined : 'button'}
                    tabIndex={isSuperAdmin ? undefined : 0}
                    onKeyDown={(event) => {
                      if (isSuperAdmin) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleCardClick();
                      }
                    }}
                  >
                    {isActivePlan && (
                      <div className={styles.activeBadge}>ACTIVE PLAN</div>
                    )}
                    <Content name={name} description={description} />
                  </Card>
                );
              })}
        </div>
      )}

    </PageContainer>
  );
}
